import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  BookOpen,
  Users,
  Clock,
  Calendar,
  User,
  GraduationCap,
} from "lucide-react";
import { getClass } from "../../services/classes";
import { ClassWithDetails } from "../../types/class";

interface ClassDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
}

const ClassDetailsModal: React.FC<ClassDetailsModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  const { data: classResponse, isLoading } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId),
    enabled: isOpen && !!classId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const classData = classResponse?.class;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Class Details
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : classData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                    Basic Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Class Name
                      </label>
                      <p className="text-sm text-gray-900">{classData.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Grade
                      </label>
                      <p className="text-sm text-gray-900">{classData.grade}</p>
                    </div>
                    {classData.section && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Section
                        </label>
                        <p className="text-sm text-gray-900">
                          {classData.section}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Capacity
                      </label>
                      <p className="text-sm text-gray-900">
                        {classData.capacity} students
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Status
                      </label>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (classData as any).status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {(classData as any).status === "active"
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Enrollment Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-green-600" />
                    Enrollment
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Current Students
                      </label>
                      <p className="text-sm text-gray-900">
                        {(classData as any).currentEnrollment ||
                          (classData as any).activeStudents?.length ||
                          classData.students?.length ||
                          0}{" "}
                        students
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Available Spots
                      </label>
                      <p className="text-sm text-gray-900">
                        {(classData as any).availableSeats ||
                          classData.capacity -
                            ((classData as any).currentEnrollment ||
                              (classData as any).activeStudents?.length ||
                              classData.students?.length ||
                              0)}{" "}
                        spots
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Utilization Rate
                      </label>
                      <div className="flex items-center space-x-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${
                                (((classData as any).currentEnrollment ||
                                  (classData as any).activeStudents?.length ||
                                  classData.students?.length ||
                                  0) /
                                  classData.capacity) *
                                100
                              }%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-900">
                          {Math.round(
                            (((classData as any).currentEnrollment ||
                              (classData as any).activeStudents?.length ||
                              classData.students?.length ||
                              0) /
                              classData.capacity) *
                              100
                          )}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Teacher Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <User className="h-5 w-5 mr-2 text-purple-600" />
                    Teaching Staff
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Class Teacher
                      </label>
                      <p className="text-sm text-gray-900">
                        {(classData as any).classTeacherId &&
                        (classData as any).classTeacherId.userId
                          ? `${
                              (classData as any).classTeacherId.userId.firstName
                            } ${
                              (classData as any).classTeacherId.userId.lastName
                            }`
                          : "No teacher assigned"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Subject Teachers
                      </label>
                      {(classData as any).subjects &&
                      (classData as any).subjects.length > 0 ? (
                        <div className="space-y-2">
                          {(classData as any).subjects.map(
                            (subject: any, index: number) => (
                              <div
                                key={index}
                                className="bg-white p-3 rounded border"
                              >
                                <div className="font-medium text-gray-900 mb-2">
                                  {subject.name || subject.subjectName}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {subject.assignedTeacherIds &&
                                  subject.assignedTeacherIds.length > 0 ? (
                                    subject.assignedTeacherIds.map(
                                      (teacher: any, teacherIndex: number) => (
                                        <span
                                          key={teacherIndex}
                                          className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800"
                                        >
                                          {teacher.userId
                                            ? `${teacher.userId.firstName} ${teacher.userId.lastName}`
                                            : teacher._id
                                            ? `Teacher ID: ${teacher._id}`
                                            : "Unknown Teacher"}
                                        </span>
                                      )
                                    )
                                  ) : (
                                    <span className="text-sm text-gray-500">
                                      No teacher assigned
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">
                          No subjects assigned
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-orange-600" />
                    Schedule
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {classData.schedule && classData.schedule.length > 0 ? (
                      <div className="space-y-2">
                        {classData.schedule.map((period, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center text-sm"
                          >
                            <span className="font-medium capitalize">
                              {period.day}
                            </span>
                            <span className="text-gray-600">
                              {period.startTime} - {period.endTime}
                            </span>
                            <span className="text-blue-600">
                              {period.subjectName}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-900">No schedule set</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Class not found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  The requested class could not be loaded.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassDetailsModal;
